import { CrewMerchantUi } from 'corso-types';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { TextInput } from 'corso-admin-ui';

type MinimumRequirement = NonNullable<
  z.infer<typeof CrewMerchantUi.amountOffOrderRawSchema>['minimumRequirement']
>;

type Selection = 'none' | MinimumRequirement['kind'];

const options: { value: Selection; label: string }[] = [
  { value: 'none', label: 'No minimum requirements' },
  { value: 'subtotal', label: 'Minimum purchase amount ($)' },
  { value: 'quantity', label: 'Minimum quantity of items' },
];

const selectionFor = (value: MinimumRequirement | undefined): Selection =>
  value?.kind ?? 'none';

const defaultsFor = (selection: Selection): MinimumRequirement | undefined =>
  match(selection)
    .with('none', () => undefined)
    .with(
      'subtotal',
      () =>
        ({
          kind: 'subtotal',
          subtotal: { greaterThanOrEqualToSubtotal: '0.00' },
        }) as const,
    )
    .with(
      'quantity',
      () =>
        ({
          kind: 'quantity',
          // Shopify's UnsignedInt64 scalar — wire format is a numeric string.
          quantity: { greaterThanOrEqualToQuantity: '1' },
        }) as const,
    )
    .exhaustive();

/**
 * Pure component for the "Minimum purchase requirements" fieldset shared
 * across discount definition forms. The parent wraps this in a single
 * `<Controller>` over the optional `minimumRequirement` container path.
 *
 * Emits `undefined` when "No minimum requirements" is selected so the
 * outgoing payload omits the field entirely (matching Shopify's expectation
 * for the absent case).
 */
export function MinimumRequirementFieldset({
  value,
  onChange,
  currencySymbol,
}: {
  value: MinimumRequirement | undefined;
  onChange: (next: MinimumRequirement | undefined) => void;
  currencySymbol: string;
}) {
  const selection = selectionFor(value);

  const handleSelectionChange = (next: Selection) => {
    onChange(defaultsFor(next));
  };

  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-2 text-sm leading-6 font-medium text-neutral-800">
        Minimum purchase requirements
      </legend>

      {options.map((option) => (
        <label
          key={option.value}
          htmlFor={`minimum-requirement-${option.value}`}
          className="flex cursor-pointer items-center gap-3 text-sm text-neutral-800"
        >
          <input
            id={`minimum-requirement-${option.value}`}
            type="radio"
            name="minimum-requirement"
            value={option.value}
            checked={selection === option.value}
            onChange={() => handleSelectionChange(option.value)}
            className="h-4 w-4 border-neutral-300 text-corso-blue-600 accent-corso-blue-600 focus:ring-corso-blue-600"
          />
          {option.label}
        </label>
      ))}

      {value?.kind === 'subtotal' && (
        <TextInput
          id="minimum-requirement-subtotal"
          label="Minimum purchase amount"
          labelVisuallyHidden
          required
          inputMode="decimal"
          addon={{ insideStart: currencySymbol }}
          value={value.subtotal.greaterThanOrEqualToSubtotal}
          onChange={(event) =>
            onChange({
              kind: 'subtotal',
              subtotal: { greaterThanOrEqualToSubtotal: event.target.value },
            })
          }
        />
      )}

      {value?.kind === 'quantity' && (
        <TextInput
          id="minimum-requirement-quantity"
          label="Minimum quantity of items"
          labelVisuallyHidden
          required
          inputMode="numeric"
          pattern="[1-9][0-9]*"
          value={value.quantity.greaterThanOrEqualToQuantity}
          onChange={(event) =>
            onChange({
              kind: 'quantity',
              quantity: {
                greaterThanOrEqualToQuantity: event.target.value,
              },
            })
          }
        />
      )}
    </fieldset>
  );
}
